import { useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { failedPasswordRules } from '../shared/index.js';

import { ApiError } from '../api/client.js';
import { authApi } from '../api/endpoints.js';
import { useSession } from '../auth/session.js';
import { BrandMark } from '../components/brand.js';
import { PasswordRules } from '../components/password-rules.js';
import { AuthLayout } from '../components/auth-layout.js';
import { Alert, Button, Field } from '../components/ui.js';

export function ResetPasswordPage(): ReactNode {
  const { user, isLoading } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [repeated, setRepeated] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <AuthLayout>{null}</AuthLayout>;
  if (user !== null) return <Navigate to="/" replace />;

  const unmet = failedPasswordRules(password);
  const mismatch = repeated !== '' && repeated !== password;
  const ready = token !== '' && unmet.length === 0 && password === repeated && repeated !== '';

  const onSubmit = async (): Promise<void> => {
    setAttempted(true);
    setProblem(null);

    if (!ready) return;

    setBusy(true);

    try {
      await authApi.resetPassword(token, password);

      void navigate('/sign-in', {
        replace: true,
        state: { notice: 'Your password is set. Sign in with it to carry on.' },
      });
    } catch (error: unknown) {
      setProblem(
        error instanceof ApiError
          ? error.message
          : 'That did not work. Request a new link and try again.',
      );
      setBusy(false);
    }
  };

  if (token === '') {
    return (
      <AuthLayout>
        <BrandMark className="mb-6" />
        <h2 className="text-[28px] font-bold tracking-[-0.03em] text-navy">Link is incomplete</h2>
        <p className="mt-1.5 mb-6 text-[14px] leading-snug text-muted">
          This address is missing its reset token. Open the link from the email exactly as it
          arrived, or ask for a new one.
        </p>

        <Button
          size="lg"
          className="w-full"
          onClick={() => {
            void navigate('/sign-in', { replace: true });
          }}
        >
          Back to sign in
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <BrandMark className="mb-6" />
      <h2 className="text-[28px] font-bold tracking-[-0.03em] text-navy">Choose a new password</h2>
      <p className="mt-1.5 mb-6 text-[14px] leading-snug text-muted">
        This link works once. Setting a password here signs out every other session on the account.
      </p>

      {problem === null ? null : <Alert tone="error">{problem}</Alert>}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        noValidate
      >
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />

        <PasswordRules value={password} showFailures={attempted} />

        <Field
          label="Repeat the new password"
          type="password"
          autoComplete="new-password"
          value={repeated}
          onChange={(event) => {
            setRepeated(event.target.value);
          }}
          {...(mismatch ? { error: 'The two do not match.' } : {})}
        />

        <Button type="submit" size="lg" loading={busy} disabled={!ready} className="w-full">
          Set the password
        </Button>
      </form>

      <p className="mt-5 text-center text-[12px] tracking-[0.04em] text-muted">
        Silver Bell Group · Task Management
      </p>
    </AuthLayout>
  );
}
